<script lang="ts">
  import { createQuery, createMutation, useQueryClient } from '@tanstack/svelte-query';
  import { ArrowRight, CheckCircle2 } from 'lucide-svelte';
  import { ticketsApi } from '$lib/api/tickets';
  import { projectsApi } from '$lib/api/projects';
  import { membersApi } from '$lib/api/members';
  import { getErrorMessage } from '$lib/api/client';
  import { route } from '$lib/state/route.svelte';
  import { toast } from '$lib/state/toast.svelte';
  import EmptyState from '$lib/components/ui/EmptyState.svelte';
  import SrcBadge from './SrcBadge.svelte';
  import SlaChip from './SlaChip.svelte';
  import Chip from '$lib/components/ui/Chip.svelte';
  import type { Ticket } from '$lib/types';

  const queryClient = useQueryClient();

  const inboxQuery = createQuery(() => ({
    queryKey: ['tickets-inbox'],
    queryFn: () => ticketsApi.inbox()
  }));
  const projectsQuery = createQuery(() => ({
    queryKey: ['projects'],
    queryFn: () => projectsApi.list()
  }));

  const assignMutation = createMutation(() => ({
    mutationFn: ({ id, projectId }: { id: number; projectId: number }) => ticketsApi.assign(id, projectId),
    onSuccess: async (data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['tickets-inbox'] });
      queryClient.invalidateQueries({ queryKey: ['tickets-inbox-count'] });
      queryClient.invalidateQueries({ queryKey: ['project-tickets', vars.projectId] });
      const p = projectsQuery.data?.results.find((x) => x.id === vars.projectId);
      // Server auto-assigns the first member granted to the project — look
      // their name up for the "— now on {name}'s board" toast suffix.
      let suffix = '';
      if (data.assignee_user_id) {
        const members = await membersApi.forProject(vars.projectId);
        const dev = members.results.find((u) => u.id === data.assignee_user_id);
        if (dev) suffix = ` — now on ${dev.name.split(' ')[0]}'s board`;
      }
      toast.success(`Assigned to ${p?.name ?? 'project'}${suffix}`);
    },
    onError: (err: unknown) => toast.error(getErrorMessage(err))
  }));

  function onAssignSelect(t: Ticket, e: Event) {
    const projectId = Number((e.target as HTMLSelectElement).value);
    if (!projectId) return;
    assignMutation.mutate({ id: t.id, projectId });
    (e.target as HTMLSelectElement).value = '';
  }

  const list = $derived(inboxQuery.data?.results ?? []);
</script>

<div class="page-head">
  <div>
    <h1 class="h-title">Ticket inbox</h1>
    <div class="h-sub">
      {list.length ? `${list.length} unassigned ticket${list.length > 1 ? 's' : ''} from the company CRM` : 'Everything triaged'}
    </div>
  </div>
</div>

{#if inboxQuery.isLoading}
  <p style="color:var(--t3);font-size:13.5px">Loading tickets…</p>
{:else if inboxQuery.isError}
  <p style="color:var(--danger);font-size:13.5px">{getErrorMessage(inboxQuery.error)}</p>
{:else if list.length}
  <div class="list">
    {#each list as t (t.id)}
      <div class="card" style="padding:16px 18px">
        <div class="rc-top">
          <div class="rc-title" style="flex:1">{t.title}</div>
          <SlaChip status={t.status} createdAt={t.created_at} />
          <Chip label="New" kind="teal" dot />
        </div>
        <div style="margin:11px 0 13px">
          <SrcBadge ticket={t} redact={false} />
        </div>
        {#if t.desc}
          <div style="font-size:13px;color:var(--t2);line-height:1.55;margin-bottom:14px">{t.desc}</div>
        {/if}
        <div style="display:flex;gap:9px;align-items:center;flex-wrap:wrap">
          <span style="font-size:12.5px;color:var(--t3);font-weight:500">Assign to project</span>
          <select class="select" style="width:auto;min-width:220px" value="" onchange={(e) => onAssignSelect(t, e)}>
            <option value="">Choose a project…</option>
            {#each projectsQuery.data?.results ?? [] as p (p.id)}
              <option value={p.id}>{p.name}</option>
            {/each}
          </select>
          <button type="button" class="btn btn-ghost btn-sm" style="margin-left:auto" onclick={() => route.push({ tab: 'ticket', ticketId: t.id })}>
            Open <ArrowRight size={15} />
          </button>
        </div>
      </div>
    {/each}
  </div>
{:else}
  <EmptyState
    icon={CheckCircle2}
    heading="Inbox zero"
    sub="No unassigned tickets. Triaged tickets appear on their project boards."
  />
{/if}
